export type TrendDirection = "up" | "down" | "stable";
export type RiskLevel = "low" | "medium" | "high";

export interface MarketSnapshotItem {
  id: string;
  label: string;
  value: string;
  direction: TrendDirection;
  period: string;
}

export interface TrendPulseItem {
  id: string;
  metric: string;
  d7: TrendDirection;
  d30: TrendDirection;
  d90: TrendDirection;
}

export interface MarketNewsItem {
  id: string;
  title: string;
  tag: "Policy" | "Exports" | "Disease" | "InputCosts" | "Demand";
  publishedAt: string;
  source: string;
}

export interface RiskSignalItem {
  id: string;
  region: string;
  level: RiskLevel;
  context: string;
}

export interface PolicyAlertItem {
  id: string;
  title: string;
  severity: "info" | "warning";
  publishedAt: string;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getMarketSnapshot = async (): Promise<MarketSnapshotItem[]> => {
  await delay(250);
  return [
    {id: "snap-1", label: "Shrimp Demand Index", value: "72", direction: "up", period: "7D"},
    {id: "snap-2", label: "Export Momentum", value: "Moderate", direction: "stable", period: "30D"},
    {id: "snap-3", label: "Input Cost Pressure", value: "High", direction: "up", period: "30D"},
  ];
};

export const getTrendPulse = async (): Promise<TrendPulseItem[]> => {
  await delay(300);
  return [
    {id: "trend-1", metric: "Farm-gate shrimp sentiment", d7: "up", d30: "up", d90: "stable"},
    {id: "trend-2", metric: "Feed cost trend", d7: "up", d30: "up", d90: "up"},
    {id: "trend-3", metric: "Harvest volume trend", d7: "stable", d30: "up", d90: "up"},
  ];
};

export const getPublicNews = async (): Promise<MarketNewsItem[]> => {
  await delay(350);
  return [
    {
      id: "news-1",
      title: "Regional buyers report firmer demand for medium count shrimp.",
      tag: "Demand",
      publishedAt: "2h ago",
      source: "Market Desk",
    },
    {
      id: "news-2",
      title: "Export documentation checks tightened in key destination markets.",
      tag: "Policy",
      publishedAt: "5h ago",
      source: "Trade Watch",
    },
    {
      id: "news-3",
      title: "Feed ingredient costs remain elevated this week.",
      tag: "InputCosts",
      publishedAt: "8h ago",
      source: "Input Monitor",
    },
  ];
};

export const getRiskSignals = async (): Promise<RiskSignalItem[]> => {
  await delay(280);
  return [
    {id: "risk-1", region: "West Coast Cluster", level: "medium", context: "Rainfall variability expected this week."},
    {id: "risk-2", region: "Delta Belt", level: "high", context: "Temperature stress may affect pond performance."},
    {id: "risk-3", region: "Southern Corridor", level: "low", context: "Conditions stable with low disruption risk."},
  ];
};

export const getPolicyAlerts = async (): Promise<PolicyAlertItem[]> => {
  await delay(220);
  return [
    {id: "policy-1", title: "Draft advisory on traceability compliance published.", severity: "info", publishedAt: "Today"},
    {id: "policy-2", title: "Port clearance timelines updated for selected routes.", severity: "warning", publishedAt: "Yesterday"},
  ];
};

// Phase 2 (deferred): public market prices endpoint with showPublic filtering.
